import { Check, Eye, EyeOff, Mail, Lock, User as UserIcon } from 'lucide-react';
import {
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { Button } from '../../../../components/atoms/button/button';
import { IconButton } from '../../../../components/atoms/icon-button/icon-button';
import { Input } from '../../../../components/atoms/input/input';
import { Modal } from '../../../../components/molecules/modal/modal';
import { Permission, groupByModule } from '../../../../types/permission';
import { CreateUsuarioPayload } from '../../../../types/usuario';
import { PermissionGroup } from './permission-group';

interface Props {
  isOpen: boolean;
  permissions: Permission[];
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: (payload: CreateUsuarioPayload) => Promise<unknown> | void;
  onClose: () => void;
}

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (values: {
  name: string;
  email: string;
  password: string;
}): FieldErrors => {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = 'Nome é obrigatório';
  else if (values.name.trim().length < 2) errors.name = 'Nome muito curto';

  if (!values.email.trim()) errors.email = 'E-mail é obrigatório';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'E-mail inválido';

  if (!values.password) errors.password = 'Senha é obrigatória';
  else if (values.password.length < 8)
    errors.password = 'A senha deve ter no mínimo 8 caracteres';

  return errors;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;

const PermissionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
`;

const ErrorAlert = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

export const CreateUserModal = ({
  isOpen,
  permissions,
  loading,
  errorMessage,
  onConfirm,
  onClose,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<FieldErrors>({});

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const grouped = useMemo(() => groupByModule(permissions), [permissions]);

  const resetForm = () => {
    if (nameRef.current) nameRef.current.value = '';
    if (emailRef.current) emailRef.current.value = '';
    if (passwordRef.current) passwordRef.current.value = '';
    setSelectedPerms(new Set());
    setShowPassword(false);
    setErrors({});
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = {
      name: nameRef.current?.value ?? '',
      email: emailRef.current?.value ?? '',
      password: passwordRef.current?.value ?? '',
    };
    const fieldErrors = validate(values);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    await onConfirm({
      name: values.name.trim(),
      email: values.email.trim(),
      password: values.password,
      permissions: Array.from(selectedPerms),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Novo usuário interno"
      description="Defina dados de acesso e as permissões iniciais."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="create-user-form"
            loading={loading}
            startIcon={!loading ? <Check size={14} /> : undefined}
            data-testid="create-user-action"
          >
            {loading ? 'Criando…' : 'Criar usuário'}
          </Button>
        </>
      }
    >
      <Body
        as="form"
        id="create-user-form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Field>
          <FieldLabel htmlFor="create-user-name">Nome</FieldLabel>
          <Input
            ref={nameRef}
            id="create-user-name"
            name="name"
            placeholder="Nome completo"
            defaultValue=""
            hasError={Boolean(errors.name)}
            startIcon={<UserIcon size={16} />}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'create-user-name-error' : undefined}
          />
          {errors.name && (
            <FieldError id="create-user-name-error">{errors.name}</FieldError>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="create-user-email">E-mail</FieldLabel>
          <Input
            ref={emailRef}
            id="create-user-email"
            name="email"
            type="email"
            placeholder="email@vendepay.com.br"
            defaultValue=""
            hasError={Boolean(errors.email)}
            startIcon={<Mail size={16} />}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'create-user-email-error' : undefined}
          />
          {errors.email && (
            <FieldError id="create-user-email-error">{errors.email}</FieldError>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="create-user-password">Senha inicial</FieldLabel>
          <Input
            ref={passwordRef}
            id="create-user-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Mínimo 8 caracteres"
            defaultValue=""
            hasError={Boolean(errors.password)}
            startIcon={<Lock size={16} />}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={
              errors.password ? 'create-user-password-error' : undefined
            }
            endIcon={
              <IconButton
                label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setShowPassword((s) => !s)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </IconButton>
            }
          />
          {errors.password && (
            <FieldError id="create-user-password-error">
              {errors.password}
            </FieldError>
          )}
        </Field>

        <FieldLabel>Permissões</FieldLabel>
        <PermissionsBlock>
          {Object.entries(grouped).map(([module, perms]) => (
            <PermissionGroup
              key={module}
              module={module}
              permissions={perms}
              selected={selectedPerms}
              onChange={setSelectedPerms}
            />
          ))}
        </PermissionsBlock>

        {errorMessage && <ErrorAlert role="alert">{errorMessage}</ErrorAlert>}
      </Body>
    </Modal>
  );
};
